import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio, CalendarClock, History, PlayCircle } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

// CONSUMER-REPLAY-SELLER-PROFILE-SCHEDULE-1 · 판매자 프로필 전용 편성표(해당 seller_id 만) · 프로필 리뉴얼 시 그대로 이동 가능.
const KST = { timeZone: 'Asia/Seoul' };
export const fmtKst = (s) => (s ? new Date(s).toLocaleString('ko-KR', { ...KST, month: 'short', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }) : '-');
const fmtMin = (sec) => (sec ? `${Math.floor(sec / 60)}분 ${Math.round(sec % 60)}초` : null);

export const useSellerSchedule = (sellerId) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let off = false;
    api.get(`/public/sellers/${sellerId}/broadcast-schedule`).then(({ data: d }) => { if (!off) setData(d); })
      .catch(() => { if (!off) setData({ live: [], upcoming: [], past: [] }); });
    return () => { off = true; };
  }, [sellerId]);
  return data;
};

const Card = ({ b }) => (
  <div className="flex gap-3 p-2 rounded-xl border border-black/10 dark:border-white/10" data-testid={`schedule-card-${b.schedule_id}`}>
    <div className="w-14 h-14 rounded-lg overflow-hidden bg-black/5 shrink-0">{b.thumbnail && <SafeImg src={b.thumbnail} alt="" className="w-full h-full object-cover" />}</div>
    <div className="flex-1 min-w-0 space-y-0.5">
      <p className="text-sm font-bold line-clamp-2 break-all">{b.title}</p>
      <p className="text-[11px] text-gray-500">{fmtKst(b.kind === 'upcoming' ? b.scheduled_at : (b.started_at || b.scheduled_at))}{b.duration_sec ? ` · ${fmtMin(b.duration_sec)}` : ''}</p>
      {b.kind === 'live' && b.watch_session_id && <Link to={`/live-view/${b.watch_session_id}`} className="inline-flex items-center gap-1 text-xs font-black text-white bg-[#FF3B30] px-2.5 py-1 rounded-full" data-testid={`schedule-watch-${b.schedule_id}`}><Radio size={11} />LIVE 시청하기</Link>}
      {b.kind === 'past' && (b.replay_session_id
        ? <Link to={`/replay/${b.replay_session_id}`} className="inline-flex items-center gap-1 text-xs font-bold text-[#FF3B30]" data-testid={`schedule-replay-${b.schedule_id}`}><PlayCircle size={12} />다시보기</Link>
        : <span className="text-[11px] text-gray-400">방송 종료</span>)}
    </div>
  </div>
);

const TABS = [['upcoming', '방송 예정', CalendarClock], ['live', '방송 중', Radio], ['past', '지난 방송', History]];

export const SellerBroadcastSchedule = ({ sellerId }) => {
  const data = useSellerSchedule(sellerId);
  const [tab, setTab] = useState(null);
  if (!data || (!data.live.length && !data.upcoming.length && !data.past.length)) return null;
  const cur = tab || (data.live.length ? 'live' : data.upcoming.length ? 'upcoming' : 'past');
  return (
    <section data-testid="seller-broadcast-schedule">
      <h2 className="text-lg font-black mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>방송 편성표</h2>
      <div className="flex gap-1 mb-3">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1 ${cur === k ? 'bg-black text-white dark:bg-white dark:text-black' : 'bg-black/5 dark:bg-white/10'}`} data-testid={`schedule-tab-${k}`}>
            <Icon size={12} />{l} {data[k].length}
          </button>
        ))}
      </div>
      {!data[cur].length && <p className="text-xs text-gray-500 py-4" data-testid="schedule-empty">표시할 방송이 없습니다.</p>}
      <div className="grid gap-2 sm:grid-cols-2">{data[cur].map((b) => <Card key={b.schedule_id} b={b} />)}</div>
    </section>
  );
};

export const SellerReplayList = ({ sellerId }) => {
  const data = useSellerSchedule(sellerId);
  const items = (data?.past || []).filter((b) => b.replay_session_id);
  if (!items.length) return null;
  return (
    <section data-testid="seller-replay-list">
      <h2 className="text-lg font-black mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>지난 방송 다시보기</h2>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {items.map((b) => (
          <Link key={b.schedule_id} to={`/replay/${b.replay_session_id}`} className="w-36 shrink-0" data-testid={`replay-card-${b.schedule_id}`}>
            <div className="relative aspect-[9/16] rounded-xl overflow-hidden bg-black">
              {b.thumbnail && <SafeImg src={b.thumbnail} alt="" className="w-full h-full object-cover opacity-70" />}
              <span className="absolute top-1.5 left-1.5 text-[10px] font-black bg-white/90 text-black px-1.5 py-0.5 rounded">다시보기</span>
              <PlayCircle size={28} className="absolute inset-0 m-auto text-white" />
            </div>
            <p className="text-xs font-bold mt-1 line-clamp-2">{b.title}</p>
            <p className="text-[10px] text-gray-500">{fmtKst(b.started_at || b.scheduled_at)}</p>
          </Link>
        ))}
      </div>
    </section>
  );
};
